import { readFile, readdir, stat } from "node:fs/promises";
import type { RunnerMode, TaskContext } from "@project/shared";

interface ResolvedEntry {
  type: "rule" | "file" | "folder" | "doc";
  path: string;
  label?: string;
  /** One-line summary shown in the discovery blurb (never the full body). */
  summary: string | null;
}

interface ResolvedTagContext {
  tagName: string;
  description: string | null;
  entries: ResolvedEntry[];
}

const TYPE_PRIORITY: Record<string, number> = { rule: 0, file: 1, folder: 2, doc: 3 };

// A single reference is described by one short line — no full-file bodies are
// ever injected. This caps how much of a file we scan to derive that line.
const SUMMARY_SCAN_CHARS = 4_000;
const SUMMARY_MAX_CHARS = 160;

// Binary file extensions to skip content injection
const BINARY_EXTENSIONS = new Set([
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".ico",
  ".svg",
  ".bmp",
  ".mp3",
  ".mp4",
  ".wav",
  ".avi",
  ".mov",
  ".pdf",
  ".zip",
  ".tar",
  ".gz",
  ".woff",
  ".woff2",
  ".ttf",
  ".eot",
  ".otf",
  ".exe",
  ".dll",
  ".so",
  ".dylib",
  ".wasm",
]);

function isBinaryPath(filePath: string): boolean {
  const ext = filePath.slice(filePath.lastIndexOf(".")).toLowerCase();
  return BINARY_EXTENSIONS.has(ext);
}

// Module-scope cache of derived summaries keyed by absolute path.
// Entries are invalidated when mtime changes (post-merge rule updates)
// and can be cleared wholesale on session boundaries via clearTagContextCache().
const fileSummaryCache = new Map<string, { mtimeMs: number; summary: string | null }>();
const folderListingCache = new Map<string, { mtimeMs: number; listing: string }>();
let fileReadCount = 0;
let folderReadCount = 0;

export function clearTagContextCache(): void {
  fileSummaryCache.clear();
  folderListingCache.clear();
}

export function _getTagContextCacheStatsForTest(): {
  files: number;
  folders: number;
  fileReads: number;
  folderReads: number;
} {
  return {
    files: fileSummaryCache.size,
    folders: folderListingCache.size,
    fileReads: fileReadCount,
    folderReads: folderReadCount,
  };
}

export function _resetTagContextReadCountsForTest(): void {
  fileReadCount = 0;
  folderReadCount = 0;
}

/**
 * Derive a one-line description for a reference file WITHOUT injecting its body.
 * Preference order: frontmatter `description:` / `title:` → first markdown
 * heading → first non-empty content line. YAML frontmatter (loader directives
 * like `paths:`) is stripped so it never leaks into the prompt.
 */
export function deriveSummary(raw: string): string | null {
  let body = raw;
  let frontmatter = "";
  if (raw.startsWith("---")) {
    const end = raw.indexOf("\n---", 3);
    if (end !== -1) {
      frontmatter = raw.slice(3, end);
      const afterClose = raw.indexOf("\n", end + 1);
      body = afterClose === -1 ? "" : raw.slice(afterClose + 1);
    }
  }

  const fmDescription = frontmatter
    .split("\n")
    .map((l) => l.trim())
    .find((l) => /^(description|title):/i.test(l));
  if (fmDescription) {
    const value = fmDescription
      .slice(fmDescription.indexOf(":") + 1)
      .trim()
      .replace(/^["']|["']$/g, "");
    if (value) return truncateSummary(value);
  }

  for (const rawLine of body.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;
    // Strip markdown heading markers / list bullets for a clean one-liner.
    const cleaned = line
      .replace(/^#+\s*/, "")
      .replace(/^[-*]\s+/, "")
      .trim();
    if (cleaned) return truncateSummary(cleaned);
  }
  return null;
}

function truncateSummary(text: string): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  return collapsed.length > SUMMARY_MAX_CHARS
    ? collapsed.slice(0, SUMMARY_MAX_CHARS - 1).trimEnd() + "…"
    : collapsed;
}

async function readFileSummary(filePath: string): Promise<string | null> {
  try {
    if (isBinaryPath(filePath)) return null;
    let mtimeMs: number;
    try {
      const st = await stat(filePath);
      mtimeMs = st.mtimeMs;
    } catch {
      return null;
    }
    const cached = fileSummaryCache.get(filePath);
    if (cached && cached.mtimeMs === mtimeMs) {
      return cached.summary;
    }
    const raw = await readFile(filePath, "utf-8");
    fileReadCount++;
    // Only scan a bounded prefix — enough to find a description/heading.
    const summary = deriveSummary(raw.slice(0, SUMMARY_SCAN_CHARS));
    fileSummaryCache.set(filePath, { mtimeMs, summary });
    return summary;
  } catch {
    return null;
  }
}

async function readFolderListing(folderPath: string): Promise<string | null> {
  try {
    let mtimeMs: number;
    try {
      const st = await stat(folderPath);
      mtimeMs = st.mtimeMs;
    } catch {
      return null;
    }
    const cached = folderListingCache.get(folderPath);
    if (cached && cached.mtimeMs === mtimeMs) {
      return cached.listing;
    }
    const entries = await readdir(folderPath);
    folderReadCount++;
    const listing = `Files: ${entries.join(", ")}`;
    folderListingCache.set(folderPath, { mtimeMs, listing });
    return listing;
  } catch {
    return null;
  }
}

async function resolveEntry(entry: {
  type: string;
  path: string;
  label?: string;
}): Promise<ResolvedEntry> {
  const result: ResolvedEntry = {
    type: entry.type as ResolvedEntry["type"],
    path: entry.path,
    label: entry.label,
    summary: null,
  };

  // A human-authored label is the best one-liner; skip disk I/O when present.
  if (entry.label) {
    result.summary = truncateSummary(entry.label);
    return result;
  }

  if (entry.type === "doc") {
    // doc type references project documents in DB — no local file to summarize.
    return result;
  }

  if (entry.type === "folder") {
    result.summary = await readFolderListing(entry.path);
    return result;
  }

  // rule or file — derive a one-line summary from disk (body is never injected)
  result.summary = await readFileSummary(entry.path);
  return result;
}

function formatEntry(entry: ResolvedEntry): string {
  const suffix = entry.summary ? ` — ${entry.summary}` : "";
  return `- \`${entry.path}\`${suffix}`;
}

function formatResolvedTags(resolved: ResolvedTagContext[]): string {
  const parts: string[] = [
    `\n## Reference Guides (load on demand)`,
    `These docs match this task's tags. They auto-load when you edit matching files, and you can Read any of them the moment you need its detail — do NOT read them all up front.`,
  ];

  for (const tag of resolved) {
    if (tag.entries.length === 0) continue;
    const desc = tag.description ? ` — ${tag.description}` : "";
    parts.push(`\n### Tag: "${tag.tagName}"${desc}`);
    for (const entry of tag.entries) {
      parts.push(formatEntry(entry));
    }
  }

  return parts.join("\n");
}

export async function resolveTagContext(
  projectTags: TaskContext["projectTags"],
  taskTagIds: string[],
  _model: string,
  _betas?: string[],
  _runnerMode?: RunnerMode,
): Promise<{ injectedSection: string; stats: { injected: number; skipped: number } }> {
  if (!projectTags?.length || !taskTagIds.length) {
    return { injectedSection: "", stats: { injected: 0, skipped: 0 } };
  }

  const taskTagIdSet = new Set(taskTagIds);
  const assignedTags = projectTags.filter((t) => taskTagIdSet.has(t.id));

  if (assignedTags.length === 0) {
    return { injectedSection: "", stats: { injected: 0, skipped: 0 } };
  }

  // Collect all entries across tags, sorted by type priority
  const allEntries: { tagIndex: number; entry: { type: string; path: string; label?: string } }[] =
    [];
  for (let i = 0; i < assignedTags.length; i++) {
    const tag = assignedTags[i];
    if (!tag.contextPaths?.length) continue;
    const sorted = [...tag.contextPaths].sort(
      (a, b) => (TYPE_PRIORITY[a.type] ?? 99) - (TYPE_PRIORITY[b.type] ?? 99),
    );
    for (const entry of sorted) {
      allEntries.push({ tagIndex: i, entry });
    }
  }

  if (allEntries.length === 0) {
    return { injectedSection: "", stats: { injected: 0, skipped: 0 } };
  }

  let injected = 0;
  let skipped = 0;

  const resolved: ResolvedTagContext[] = assignedTags.map((t) => ({
    tagName: t.name,
    description: t.description,
    entries: [],
  }));

  for (const { tagIndex, entry } of allEntries) {
    const result = await resolveEntry(entry);
    resolved[tagIndex].entries.push(result);
    if (result.summary === null) {
      skipped++;
    } else {
      injected++;
    }
  }

  return {
    injectedSection: formatResolvedTags(resolved),
    stats: { injected, skipped },
  };
}
